// Constantes et helpers partagés du module Sous-traitants
import axios from 'axios';

export const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const FORMES_JURIDIQUES = {
  auto_entrepreneur: 'Auto-entrepreneur',
  ei: 'Entreprise individuelle',
  eurl: 'EURL',
  sarl: 'SARL',
  sas: 'SAS',
  sasu: 'SASU',
  sa: 'SA',
  autre: 'Autre'
};

export const CONFORMITE = {
  conforme: { label: 'Conforme', color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  a_renouveler: { label: 'À renouveler', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  non_conforme: { label: 'Non conforme', color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' }
};

export const STATUTS_ST = {
  actif: { label: 'Actif', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  suspendu: { label: 'Suspendu', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' },
  archive: { label: 'Archivé', color: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' }
};

export const STATUT_DOCUMENT = {
  valide: { label: 'Valide', color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  bientot_expire: { label: 'Expire bientôt', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  expire: { label: 'Expiré', color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  manquant: { label: 'Manquant', color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' }
};

export const STATUT_VERIFICATION = {
  a_verifier: { label: 'À vérifier', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  verifie: { label: 'Vérifié', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  rejete: { label: 'Rejeté', color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' }
};

export const METHODES_VERIFICATION = {
  code_urssaf: 'Code de sécurité URSSAF',
  visuel: 'Contrôle visuel',
  autre: 'Autre'
};

export const TYPES_CONTROLE = {
  initial: 'Initial (signature du contrat)',
  periodique: 'Périodique (6 mois)',
  ponctuel: 'Ponctuel'
};

export const RESULTATS_CONTROLE = {
  conforme: { label: 'Conforme', color: CONFORMITE.conforme.color },
  partiel: { label: 'Partiellement conforme', color: CONFORMITE.a_renouveler.color },
  non_conforme: { label: 'Non conforme', color: CONFORMITE.non_conforme.color }
};

export const CONDITIONS = {
  '': 'Toujours exigible',
  emploie_salaries: 'Si le sous-traitant emploie des salariés',
  salaries_etrangers: 'Si le sous-traitant emploie des salariés étrangers',
  secteur_btp: 'Si le sous-traitant intervient dans le BTP'
};

export const formatDateFr = (value) => {
  if (!value) return '—';
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR');
};

export const formatDateTimeFr = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

export const formatSiret = (siret) => {
  if (!siret) return '—';
  const digits = String(siret).replace(/\D/g, '');
  if (digits.length !== 14) return siret;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
};

export const formatMontant = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
};

export const joursRestantsLabel = (jours) => {
  if (jours === null || jours === undefined) return '';
  if (jours < 0) return `expiré depuis ${Math.abs(jours)} j`;
  if (jours === 0) return "expire aujourd'hui";
  return `dans ${jours} j`;
};

/** Convertit un fichier en base64 (sans le préfixe data:) */
export const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/** Déclenche le téléchargement d'un payload { data (base64), filename, content_type } ; ouvre les PDF/images dans un onglet si demandé */
export const downloadBase64Payload = (payload, { openInTab = false } = {}) => {
  if (!payload?.data) throw new Error('Aucune donnée disponible');
  const byteCharacters = atob(payload.data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: payload.content_type || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const viewable = ['application/pdf', 'image/png', 'image/jpeg'];
  if (openInTab && viewable.includes(payload.content_type)) {
    window.open(url, '_blank');
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = payload.filename || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

/** GET d'un endpoint de téléchargement renvoyant { data, filename, content_type } */
export const fetchAndDownload = async (url, options) => {
  const response = await axios.get(url);
  downloadBase64Payload(response.data, options);
};
